import React, { useState } from "react";
import { Plus, Trash2, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { TAG_COLORS, refreshTagFamilies, useTagFamilies } from "./DossierTags";

export const SWATCH = {
  red: "bg-red-400", orange: "bg-orange-400", amber: "bg-amber-400", emerald: "bg-emerald-400", teal: "bg-teal-400",
  sky: "bg-sky-400", indigo: "bg-indigo-400", violet: "bg-violet-400", rose: "bg-rose-400", slate: "bg-slate-400",
};
const AUTO_LABEL = { produit: "Produit", sous_traitant: "Sous-traitant", regie: "Régie" };
const plural = (n) => `${n} dossier${n > 1 ? "s" : ""} concerné${n > 1 ? "s" : ""}`;

async function run(fn, ok, onChange) {
  try {
    await fn();
    await refreshTagFamilies();
    onChange?.();
    if (ok) toast.success(ok);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Action impossible");
  }
}

function TagChip({ fam, tag, idx, ti, onChange }) {
  const [name, setName] = useState(tag.name);
  const rename = () => name.trim() && name.trim() !== tag.name
    ? run(() => api.patch(`/configuration/tags/${fam.id}/tags/${tag.id}`, { name: name.trim() }), null, onChange)
    : setName(tag.name);
  const remove = () => window.confirm(`Supprimer le tag « ${tag.name} » ?\n${plural(tag.count || 0)}.`)
    && run(() => api.delete(`/configuration/tags/${fam.id}/tags/${tag.id}`), "Tag supprimé", onChange);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border text-[11px] px-2 py-0.5 font-semibold ${TAG_COLORS[fam.color]}`} data-testid={`tag-chip-${idx}-${ti}`}>
      <input value={name} size={Math.max(name.length, 3)} onChange={(e) => setName(e.target.value)} onBlur={rename} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="bg-transparent focus:outline-none" data-testid={`tag-name-${idx}-${ti}`} />
      <span className="opacity-60 tabular-nums">{tag.count || 0}</span>
      <button onClick={remove} className="opacity-60 hover:opacity-100" title="Supprimer le tag" data-testid={`tag-delete-${idx}-${ti}`}><X className="w-2.5 h-2.5" /></button>
    </span>
  );
}

function FamilyRow({ fam, idx, onChange }) {
  const [name, setName] = useState(fam.name);
  const [draft, setDraft] = useState("");
  const patch = (body) => run(() => api.patch(`/configuration/tags/families/${fam.id}`, body), null, onChange);
  const addTag = () => draft.trim() && run(() => api.post(`/configuration/tags/${fam.id}/tags`, { name: draft.trim() }), null, onChange).then(() => setDraft(""));
  const remove = () => window.confirm(`Supprimer la famille « ${fam.name} » et ses ${fam.tags.length} tag(s) ?\n${plural(fam.count || 0)}.`)
    && run(() => api.delete(`/configuration/tags/families/${fam.id}`), "Famille supprimée", onChange);
  return (
    <div className="p-3 rounded border border-norea-border bg-white space-y-2" data-testid={`tag-family-${idx}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${SWATCH[fam.color]}`} />
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => (name.trim() && name.trim() !== fam.name ? patch({ name: name.trim() }) : setName(fam.name))} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="flex-1 min-w-0 text-sm font-medium bg-transparent focus:outline-none" data-testid={`tag-family-name-${idx}`} />
        <span className="text-[10px] text-norea-textMuted tabular-nums" data-testid={`tag-family-count-${idx}`}>{fam.count || 0} dos.</span>
        {fam.auto_field ? (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800" title="Famille automatique protégée" data-testid={`tag-family-auto-${idx}`}>
            <Lock className="w-2.5 h-2.5" /> Auto : {AUTO_LABEL[fam.auto_field]}
          </span>
        ) : (
          <button onClick={remove} className="text-norea-textMuted hover:text-red-600" title="Supprimer la famille" data-testid={`tag-family-delete-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {Object.keys(SWATCH).map((c) => (
          <button key={c} onClick={() => patch({ color: c })} title={c} className={`w-3.5 h-3.5 rounded-full ${SWATCH[c]} transition-transform hover:scale-110 ${fam.color === c ? "ring-2 ring-offset-1 ring-norea-text" : ""}`} data-testid={`tag-family-color-${idx}-${c}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {fam.tags.map((t, ti) => <TagChip key={t.id} fam={fam} tag={t} idx={idx} ti={ti} onChange={onChange} />)}
      </div>
      <div className="flex gap-1.5">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="Nouveau tag…" className="flex-1 min-w-0 px-2 py-1 text-xs border border-norea-border rounded focus:ring-1 focus:ring-norea-primary outline-none" data-testid={`tag-add-input-${idx}`} />
        <button onClick={addTag} className="btn-secondary text-xs" data-testid={`tag-add-btn-${idx}`}><Plus className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

export default function TagManager({ onChange, grid = false }) {
  const fams = useTagFamilies();
  const [name, setName] = useState("");
  const addFamily = () => name.trim() && run(() => api.post("/configuration/tags/families", { name: name.trim(), color: "slate" }), "Famille créée", onChange).then(() => setName(""));
  return (
    <div className="space-y-3" data-testid="tag-manager">
      <div className="flex gap-1.5">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addFamily()} placeholder="Nouvelle famille de tags…" className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-norea-border rounded focus:ring-1 focus:ring-norea-primary outline-none" data-testid="tag-family-new-input" />
        <button onClick={addFamily} className="btn-secondary text-xs" data-testid="btn-add-tag-family"><Plus className="w-3 h-3" /> Famille</button>
      </div>
      <div className={grid ? "grid grid-cols-1 md:grid-cols-2 gap-3" : "space-y-2"}>
        {fams.map((f, i) => <FamilyRow key={`${f.id}-${f.name}`} fam={f} idx={i} onChange={onChange} />)}
      </div>
    </div>
  );
}
